// Gemeinsame Logik für Intervall-Änderung, Pause/Fortsetzen und Plattform-Löschen von
// Monitoring- und AI-Suite-Projekten. Die Fachlogik (Credit-Buchung, Cron-Flags, Anker gegen das
// Nachholen verpasster Tage) liegt in der monitoring-api; hier stehen nur Anzeige-Rechnung und
// API-Aufrufe, damit Übersicht, Projektansicht und Settings-Dialoge dieselben Werte zeigen.
import { getMonitoringBaseUrl } from "./aiSuiteProjectSettings"

// Dieselben drei Optionen wie beim Anlegen eines Projekts (monitoring/setup/CreateProject.vue,
// aisuite/CreateProject.vue).
export const INTERVAL_OPTIONS = [
    { value: 1, text: "daily" },
    { value: 7, text: "weekly" },
    { value: 28, text: "monthly" }
]

// Altprojekte haben teils 30 oder 31 statt 28 - alle drei gelten als "monthly".
const isMonthly = (interval) => {
    const iv = Number(interval)
    return iv === 28 || iv === 30 || iv === 31
}

// Index in INTERVAL_OPTIONS oder -1 bei einem Intervall, das es in der Auswahl nicht gibt.
export const intervalOptionIndex = (interval) => {
    if (isMonthly(interval)) return 2
    const iv = Number(interval)
    if (iv === 7) return 1
    if (iv === 1) return 0
    return -1
}

export const intervalLabel = (interval) => {
    const index = intervalOptionIndex(interval)
    return index > -1 ? INTERVAL_OPTIONS[index].text : Number(interval) ? "every " + Number(interval) + " days" : "-"
}

// Updates pro Monat - dieselbe Formel wie in der monitoring-api (api/helper/creditMath.js
// cronIntervalFactor) und im Setup ("Requested keyword updates").
export const intervalCreditFactor = (interval) => (isMonthly(interval) ? 1 : Number(interval) == 7 ? 4 : 30)

// Monatlicher Credit-Verbrauch eines Projekts, wie die API ihn bucht:
//   AI Suite:         Prompts  * Faktor * (Plattformen + 1 falls google)
//   Monitoring Suite: Keywords * Faktor * (2 falls AI Overviews asynchron geladen werden)
export const projectCreditUsage = (project, interval) => {
    const p = project || {}
    const isAi = p.type === "ai_tracking"
    // AI-Projekte tragen beide Felder (updateKeywordCount in der API), AIProject.vue liest prompt_count zuerst
    const count = Number(isAi ? p.prompt_count || p.keyword_count : p.keyword_count) || 0
    const multiplier = isAi ? (Array.isArray(p.platforms) ? p.platforms.length : 0) + (p.google ? 1 : 0) : p.load_async_ai_overview ? 2 : 1
    return {
        total: count * intervalCreditFactor(interval === undefined ? p.interval : interval) * multiplier,
        unit: isAi ? "prompt updates" : "keyword updates"
    }
}

export const isProjectPaused = (project) => !!(project && project.paused)

// Aktuelle Zahl der Keywords bzw. Prompts vom Server. Die Projektdaten einer offenen Ansicht sind nach
// einem Keyword-/Prompt-Upload veraltet, weil der Import danach im Hintergrund weiterläuft. Läuft er
// noch, lehnt die API Intervall und Pause ohnehin mit import_in_progress ab. null, wenn nicht lesbar.
export const fetchEntryCount = async ({ projectId, axiosPost, cryptoJS }) => {
    const data = await postToMonitoringApi({ path: "/project/one", data: { project_id: projectId }, axiosPost, cryptoJS })
    const count = Number(data && data.keyword_count)
    return data && !data.error && Number.isFinite(count) ? count : null
}

// Projekt mit aktueller Eintragszahl für projectCreditUsage (AI-Projekte lesen prompt_count zuerst)
export const withEntryCount = (project, count) => (count === null || count === undefined || !project ? project : { ...project, keyword_count: count, prompt_count: count })

// Wann eine Credit-Änderung wirkt, so wie die API bucht: Der Intervall-Anteil wird bei einer Erhöhung
// sofort reserviert, bei einer Verkürzung gibt die API die Differenz erst zum Ende des laufenden Zyklus
// frei (creditMath.buildFlatReleaseRow). Neue Plattformen reserviert sie sofort zum neuen Intervall.
//   before: Verbrauch heute, afterInterval: bisherige Plattformen mit neuem Intervall, after: Verbrauch danach
export const splitCreditChange = ({ before, afterInterval, after }) => {
    const intervalDelta = (Number(afterInterval) || 0) - (Number(before) || 0)
    const platformCost = Math.max(0, (Number(after) || 0) - (Number(afterInterval) || 0))
    return { reserveNow: Math.max(0, intervalDelta) + platformCost, releaseLater: Math.max(0, -intervalDelta) }
}

// Hält das Projekt aktuell eine Reservierung? Demo-Projekte nie, pausierte Projekte nicht mehr
// (Freigabe beim Pausieren, siehe API projectFunctions.pauseProject) - gleiche Regel wie
// creditMath.holdsReservation in der API.
export const holdsReservation = (project) => !!project && !project.demo_project && !project.paused

// Monatlicher Verbrauch von `count` Keywords/Prompts, z.B. für eine Löschung einzelner Einträge.
export const entriesCreditUsage = (project, count) => {
    const p = project || {}
    const isAi = p.type === "ai_tracking"
    const multiplier = isAi ? (Array.isArray(p.platforms) ? p.platforms.length : 0) + (p.google ? 1 : 0) : p.load_async_ai_overview ? 2 : 1
    return (Number(count) || 0) * intervalCreditFactor(p.interval) * multiplier
}

// Ende der laufenden Update-Periode: ab dann gibt der Freigabe-Cron eine alte Reservierung frei.
// Gleiche Rechnung wie die API beim Schreiben der Gutschrift-Zeile (creditController.addProjectDeletion):
// letzter verarbeiteter Lauf + Intervall, bei monatlich gleicher Tag im Folgemonat bzw. der 1. des
// übernächsten Monats, wenn der letzte Lauf am 29.-31. war.
// "Letzter Lauf" wie in der API: Monitoring = letztes Element von processed_dates; AI Suite = jüngstes
// Datum aus processed_dates und processed_dates_ai_tracking_<p> der AKTUELL eingetragenen Plattformen
// (Läufe entfernter oder deaktivierter Plattformen zählen nicht). Ohne Lauf: Anlagedatum, sonst null.
export const currentCycleEnd = (project, interval) => {
    const p = project || {}
    const toTime = (value) => {
        const t = new Date(value).getTime()
        return Number.isFinite(t) ? t : null
    }
    let latest = null
    if (p.type === "ai_tracking") {
        const keys = ["processed_dates"].concat((Array.isArray(p.platforms) ? p.platforms : []).map((platform) => "processed_dates_ai_tracking_" + platform))
        for (const key of keys) {
            for (const value of Array.isArray(p[key]) ? p[key] : []) {
                const t = toTime(value)
                if (t !== null && (latest === null || t > latest)) latest = t
            }
        }
    } else if (Array.isArray(p.processed_dates) && p.processed_dates.length) {
        latest = toTime(p.processed_dates[p.processed_dates.length - 1])
    }
    if (latest === null && p.created_date) latest = toTime(p.created_date)
    if (latest === null) return null
    const base = new Date(latest)
    const iv = interval === undefined ? p.interval : interval
    if (isMonthly(iv)) {
        if (base.getDate() > 28) return new Date(base.getFullYear(), base.getMonth() + 2, 1)
        return new Date(base.getFullYear(), base.getMonth() + 1, base.getDate())
    }
    const end = new Date(base.getTime())
    end.setDate(end.getDate() + (Number(iv) || 30))
    return end
}

// Hinweistext für Lösch-Bestätigungen: wie viele Credits die Löschung freigibt. Leer, wenn das Projekt
// nichts reserviert hat (pausiert oder Demo) - dann gibt die Löschung auch nichts frei.
export const releaseHint = (project, credits) => {
    if (!holdsReservation(project) || !(credits > 0)) return ""
    const unit = project && project.type === "ai_tracking" ? "prompt updates" : "keyword updates"
    const end = currentCycleEnd(project)
    return "Releases " + credits + " " + unit + " per month after the current update cycle" + (end ? " (" + end.toLocaleDateString("de-DE") + ")" : "") + "."
}

// Aktueller Stand des Credit-Kontingents der Firma (gleicher Endpunkt wie die Credit-Übersicht in den
// Einstellungen). Rückgabe: { count, limit, remaining } für die Suite des Projekts oder null.
export const fetchCreditState = async ({ project, axiosPost }) => {
    try {
        // 10 s Timeout: der Bestätigungsdialog sperrt "confirm" während des Ladens - ein hängender Abruf
        // darf ihn nicht dauerhaft blockieren (danach prüft die API beim Speichern ohnehin selbst)
        const response = await axiosPost(process.env.VUE_APP_APIURL + "/company/credits/get", { company_id: localStorage.company_id }, 10000)
        const data = (response && response.data) || {}
        const isAi = project && project.type === "ai_tracking"
        const bucket = isAi ? data.ks && data.ks.prompts : data.ms && data.ms.keywords
        if (!bucket || bucket.credits === undefined || bucket.count === undefined) return null
        const count = Number(bucket.count) || 0
        const limit = Number(bucket.credits) || 0
        return { count, limit, remaining: limit - count }
    } catch (error) {
        console.log("fetchCreditState failed", error)
        return null
    }
}

// Erstimport läuft noch: setup.finished wird erst am Ende des Imports gesetzt (setup.error bei
// Abbruch). Gleiche Regel wie in der API (monitoring-api api/helper/projectPause.js getRunningImport),
// inklusive der 24-Stunden-Grenze für hängengebliebene Importe. Die übrigen Import-Arten
// (Keyword-Nachtrag, Plattform-Import) sieht nur die API - sie antwortet dann mit import_in_progress.
export const isInitialImportRunning = (project) => {
    const setup = (project && project.setup) || {}
    if (setup.finished === true || setup.error) return false
    // Laufende Importe tragen einen Merker mit Lebenszeichen (setup.import_runs, last_seen alle 60 s
    // erneuert, nach 10 Minuten ohne Lebenszeichen tot) - gleiche Regel wie in der API.
    const runs = Array.isArray(setup.import_runs) ? setup.import_runs : []
    if (runs.length) {
        return runs.some((r) => r && r.type === "initial" && r.last_seen && Date.now() - new Date(r.last_seen).getTime() < 10 * 60 * 1000)
    }
    // ohne Merker (vor diesem Stand angelegt): am Anlagedatum messen
    const created = project && project.created_date ? new Date(project.created_date).getTime() : NaN
    return Number.isFinite(created) && Date.now() - created < 24 * 60 * 60 * 1000
}

// Meldung zur API-Antwort { error: "import_in_progress", import_type }.
export const importInProgressMessage = (importType) => {
    if (importType === "initial") return "This project is still being set up. Please try again once the import has finished."
    if (importType === "platform") return "A platform is currently being added to this project. Please try again in a few minutes."
    if (importType === "update") return "An update of this project is currently running. Please try again once it has finished."
    if (importType === "retarget") return "The previous interval change is still being applied. Please try again in a few minutes."
    return "Keywords are currently being imported into this project. Please try again in a few minutes."
}

const postToMonitoringApi = async ({ path, data, axiosPost, cryptoJS }) => {
    const baseUrl = getMonitoringBaseUrl({ companyIdEncrypted: localStorage.company_id, cryptoJS })
    try {
        const response = await axiosPost(baseUrl + path, { company_id: localStorage.company_id, ...data })
        return (response && response.data) || {}
    } catch (error) {
        // axios wirft bei 4xx/5xx - die API schickt auch dort { error, ... } (z.B. import_type bei 409)
        const body = error && error.response && error.response.data
        return { ...(body && typeof body === "object" ? body : {}), error: (body && body.error) || "request_failed" }
    }
}

// Antwort: { paused: true, released_credits } bzw. { resumed: true, booked_credits } oder
// { resumed: false, error: "not_enough_credits", credits, count, required }.
export const setProjectPaused = ({ projectId, paused, axiosPost, cryptoJS }) =>
    postToMonitoringApi({ path: paused ? "/project/pause" : "/project/resume", data: { project_id: projectId }, axiosPost, cryptoJS })

// Intervall über die allgemeine Settings-Route ändern - dort passieren Credit-Prüfung, Buchung
// und Task-Umstellung. Die AI-Suite-eigene Route (/ai-suite/project/update/information) übernimmt
// nur Titel und Marke und würde das Intervall stillschweigend verwerfen.
export const updateProjectInterval = ({ projectId, interval, axiosPost, cryptoJS }) =>
    postToMonitoringApi({ path: "/project/update/information", data: { project_id: projectId, fieldsToUpdate: { interval } }, axiosPost, cryptoJS })

// Antwort: { deleted: true, results, released_credits } oder { error }.
export const deleteProjectPlatform = ({ projectId, platform, axiosPost, cryptoJS }) =>
    postToMonitoringApi({ path: "/project/delete/platform", data: { project_id: projectId, platform }, axiosPost, cryptoJS })

// Meldung im Wortlaut der bestehenden Credit-Hinweise (MonitoringSetup.vue, buildCreditsNotificationMessage).
export const buildNotEnoughCreditsMessage = ({ requested, unit, responseData }) => {
    const remaining = (Number(responseData && responseData.credits) || 0) - (Number(responseData && responseData.count) || 0)
    const label = unit === "prompt updates" ? "prompt updates" : "keyword updates"
    return "You do not have enough credits left. Requested " + label + ": " + requested + ". Remaining " + label + ": " + remaining
}
